import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardCheck,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
  User,
  Mail,
  Building2,
  Briefcase,
  CheckSquare,
  Square,
  MapPin,
  Layers,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/services/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const StatBadge = ({ icon: Icon, label, value, color }) => (
  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${color}`}>
    <Icon className="h-4 w-4" strokeWidth={1.5} />
    <span className="text-sm font-medium">{label}:</span>
    <span className="font-semibold">{value}</span>
  </div>
);

const RegistrantCard = ({ registrant, onApprove, onDecline, isProcessing, approvingThis, decliningThis, isSelected, onSelectChange }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={`bg-white rounded-lg border p-6 hover:shadow-md transition-all duration-200 ${
        isSelected ? 'border-indigo-300 bg-indigo-50/30' : 'border-slate-200'
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-4 flex-1">
          {/* Checkbox */}
          <div className="pt-1">
            <Checkbox
              checked={isSelected}
              onCheckedChange={onSelectChange}
              data-testid={`select-${registrant.id}`}
            />
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <User className="h-5 w-5 text-indigo-950" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {registrant.first_name} {registrant.last_name}
                </h3>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  {registrant.registration_type_name || "Pending Review"}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mt-1">
              {registrant.email && (
                <div className="flex items-start gap-2 text-slate-600">
                  <Mail className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="break-all">{registrant.email}</span>
                </div>
              )}
              {registrant.organization && (
                <div className="flex items-start gap-2 text-slate-600">
                  <Building2 className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="break-words">{registrant.organization}</span>
                </div>
              )}
              {registrant.position && (
                <div className="flex items-start gap-2 text-slate-600">
                  <Briefcase className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="break-words">{registrant.position}</span>
                </div>
              )}
              {registrant.department && (
                <div className="flex items-start gap-2 text-slate-600">
                  <Layers className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span className="break-words">{registrant.department}</span>
                </div>
              )}
              {registrant.country && (
                <div className="flex items-start gap-2 text-slate-600">
                  <MapPin className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" strokeWidth={1.5} />
                  <span>{registrant.country}{registrant.city ? `, ${registrant.city}` : ""}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:flex-col lg:flex-row ml-8 sm:ml-0">
          <Button
            onClick={() => onApprove(registrant.id)}
            disabled={isProcessing}
            className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 sm:w-full lg:w-auto"
            data-testid={`approve-btn-${registrant.id}`}
          >
            {approvingThis ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
            )}
            Approve
          </Button>
          <Button
            onClick={() => onDecline(registrant.id)}
            disabled={isProcessing}
            variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 flex-1 sm:w-full lg:w-auto"
            data-testid={`decline-btn-${registrant.id}`}
          >
            {decliningThis ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <XCircle className="h-4 w-4 mr-2" strokeWidth={1.5} />
            )}
            Decline
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Renders the pending list + bulk action bar for a given set of registrants
const RegistrantList = ({
  registrants,
  selectedIds,
  onSelectChange,
  onSelectAll,
  onAction,
  onBulkAction,
  processingId,
  bulkProcessing,
}) => {
  const allSelected = registrants.length > 0 && selectedIds.size === registrants.length;
  const someSelected = selectedIds.size > 0;

  return (
    <div className="space-y-4">
      {/* Bulk Actions Bar */}
      {registrants.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={onSelectAll}
                className="gap-2"
                data-testid="select-all-btn"
              >
                {allSelected ? (
                  <CheckSquare className="h-4 w-4" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
                {allSelected ? "Deselect All" : "Select All"}
              </Button>
              {someSelected && (
                <span className="text-sm text-slate-600">
                  {selectedIds.size} of {registrants.length} selected
                </span>
              )}
            </div>

            {someSelected && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => onBulkAction("approve")}
                  disabled={bulkProcessing}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="bulk-approve-btn"
                >
                  {bulkProcessing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Approve Selected ({selectedIds.size})
                </Button>
                <Button
                  onClick={() => onBulkAction("decline")}
                  disabled={bulkProcessing}
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="bulk-decline-btn"
                >
                  {bulkProcessing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2" />
                  )}
                  Decline Selected ({selectedIds.size})
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending List */}
      <AnimatePresence mode="popLayout">
        {registrants.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-lg border border-slate-200 p-12 text-center"
          >
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" strokeWidth={1.5} />
            <h3
              className="text-lg font-semibold text-slate-900 mb-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              All caught up!
            </h3>
            <p className="text-slate-500">No pending registrations to review.</p>
          </motion.div>
        ) : (
          registrants.map((registrant) => (
            <RegistrantCard
              key={registrant.id}
              registrant={registrant}
              onApprove={(id) => onAction(id, "approve")}
              onDecline={(id) => onAction(id, "decline")}
              isProcessing={processingId.has(registrant.id) || bulkProcessing}
              approvingThis={processingId.has(`${registrant.id}:approve`)}
              decliningThis={processingId.has(`${registrant.id}:decline`)}
              isSelected={selectedIds.has(registrant.id)}
              onSelectChange={(checked) => onSelectChange(registrant.id, checked)}
            />
          ))
        )}
      </AnimatePresence>
    </div>
  );
};

export const ReviewerDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, declined: 0 });
  const [pendingRegistrants, setPendingRegistrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // processingIds is a Set of in-flight IDs (and "id:action" for spinner direction).
  // Using a ref-backed updater so rapid clicks always see the latest Set.
  const [processingId, setProcessingId] = useState(new Set());
  const processingIdRef = useRef(new Set());

  const addProcessing = (keys) => {
    keys.forEach(k => processingIdRef.current.add(k));
    setProcessingId(new Set(processingIdRef.current));
  };

  const removeProcessing = (keys) => {
    keys.forEach(k => processingIdRef.current.delete(k));
    setProcessingId(new Set(processingIdRef.current));
  };
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState(null);

  // Determine if we should show tabs
  // assignedTypeIds = MongoDB IDs (used as stable tab keys)
  // assignedEaTypeIds = EventsAir GUIDs (used to match registrant.registration_type_id)
  // Wrapped in useMemo so the array reference is stable across renders,
  // preventing the useEffect dependency array from firing on every render.
  const assignedTypeIds = useMemo(
    () => user?.assigned_registration_type_ids || [],
    [user]
  );
  const assignedTypeNames = useMemo(
    () => user?.assigned_registration_type_names || [],
    [user]
  );
  const assignedEaTypeIds = useMemo(
    () => user?.assigned_eventsair_type_ids || [],
    [user]
  );
  const showTabs = assignedTypeIds.length > 1;

  const fetchData = useCallback(async () => {
    try {
      const [statsData, registrantsData] = await Promise.all([
        api.registrants.stats(),
        api.registrants.list("pending"),
      ]);
      setStats(statsData);
      setPendingRegistrants(registrantsData);
      // Clear selection when data refreshes
      setSelectedIds(new Set());
    } catch (error) {
      toast.error("Failed to load data from EventsAir");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Set default active tab when types are loaded
  useEffect(() => {
    if (showTabs && assignedTypeIds.length > 0 && !activeTab) {
      setActiveTab(assignedTypeIds[0]);
    }
  }, [showTabs, assignedTypeIds, activeTab, assignedEaTypeIds]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleSelectChange = (id, checked) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleSelectAll = (registrantsForTab) => {
    if (selectedIds.size === registrantsForTab.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(registrantsForTab.map(r => r.id)));
    }
  };

  const handleAction = async (id, action) => {
    // Guard: if this registrant is already being processed, ignore the click
    if (processingIdRef.current.has(id)) return;

    // Lock the card immediately (disables buttons on any remaining visible card)
    addProcessing([id, `${id}:${action}`]);

    // ── Optimistic removal ────────────────────────────────────────────────────
    // Remove the card from the list instantly so the reviewer sees immediate
    // feedback. If the API call fails we restore it.
    const removedRegistrant = pendingRegistrants.find(r => r.id === id);
    setPendingRegistrants(prev => prev.filter(r => r.id !== id));
    setSelectedIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    // ─────────────────────────────────────────────────────────────────────────

    try {
      await api.registrants.action(id, action);
      toast.success(`Registrant ${action}d successfully - Updated in EventsAir`);
      // Refresh stats counter in the background (no await — non-blocking)
      api.registrants.stats().then(setStats).catch(() => {});
    } catch (error) {
      // Restore the card on failure so the reviewer can try again
      if (removedRegistrant) {
        setPendingRegistrants(prev => {
          // Re-insert at original position if possible, otherwise append
          return [...prev, removedRegistrant];
        });
      }
      toast.error(error.message || `Failed to ${action} registrant`);
    } finally {
      removeProcessing([id, `${id}:${action}`]);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) {
      toast.error("Please select registrants first");
      return;
    }

    setBulkProcessing(true);
    try {
      const result = await api.registrants.bulkAction(action, Array.from(selectedIds));

      if (result.success_count > 0) {
        toast.success(`${result.success_count} registrant(s) ${action}d successfully`);
      }
      if (result.failed_count > 0) {
        toast.error(`${result.failed_count} registrant(s) failed to ${action}`);
      }

      fetchData();
    } catch (error) {
      toast.error(error.message || `Failed to ${action} registrants`);
    } finally {
      setBulkProcessing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  // Filter registrants by EventsAir type GUID for a given tab
  // typeIdx is the index into assignedTypeIds (MongoDB ID used as tab key)
  const getRegistrantsForType = (mongoTypeId) => {
    const idx = assignedTypeIds.indexOf(mongoTypeId);
    const eaTypeId = idx >= 0 ? (assignedEaTypeIds[idx] || "").toUpperCase() : "";
    if (!eaTypeId) return [];
    return pendingRegistrants.filter(r => (r.registration_type_id || "").toUpperCase() === eaTypeId);
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="reviewer-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1
              className="text-2xl font-semibold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Review Queue
            </h1>
            <p className="text-slate-500 mt-1">Real-time data from EventsAir</p>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Bar */}
        <div className="flex flex-wrap gap-3">
          <StatBadge
            icon={ClipboardCheck}
            label="Pending"
            value={stats.pending}
            color="bg-amber-50 text-amber-700"
          />
          <StatBadge
            icon={CheckCircle2}
            label="Approved"
            value={stats.approved}
            color="bg-emerald-50 text-emerald-700"
          />
          <StatBadge
            icon={XCircle}
            label="Declined"
            value={stats.declined}
            color="bg-red-50 text-red-700"
          />
        </div>

        {/* Tabs (only when reviewer has more than one type) */}
        {showTabs ? (
          <Tabs
            value={activeTab || assignedTypeIds[0]}
            onValueChange={(val) => {
              setActiveTab(val);
              setSelectedIds(new Set());
            }}
          >
            <TabsList className="mb-2">
              {assignedTypeIds.map((typeId, i) => {
                const typeName = assignedTypeNames[i] || typeId;
                const count = getRegistrantsForType(typeId).length;
                return (
                  <TabsTrigger key={typeId} value={typeId} className="gap-2">
                    {typeName}
                    {count > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs font-semibold ml-1 px-1.5 py-0">
                        {count}
                      </Badge>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {assignedTypeIds.map((typeId) => {
              const registrantsForTab = getRegistrantsForType(typeId);
              return (
                <TabsContent key={typeId} value={typeId}>
                  <RegistrantList
                    registrants={registrantsForTab}
                    selectedIds={selectedIds}
                    onSelectChange={handleSelectChange}
                    onSelectAll={() => handleSelectAll(registrantsForTab)}
                    onAction={handleAction}
                    onBulkAction={handleBulkAction}
                    processingId={processingId}
                    bulkProcessing={bulkProcessing}
                  />
                </TabsContent>
              );
            })}
          </Tabs>
        ) : (
          /* Single type — original layout without tabs */
          <RegistrantList
            registrants={pendingRegistrants}
            selectedIds={selectedIds}
            onSelectChange={handleSelectChange}
            onSelectAll={() => handleSelectAll(pendingRegistrants)}
            onAction={handleAction}
            onBulkAction={handleBulkAction}
            processingId={processingId}
            bulkProcessing={bulkProcessing}
          />
        )}
      </div>
    </Layout>
  );
};
